import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  ReceiptLongRounded as ReceiptLongIcon,
  AssessmentRounded as ReportIcon,
  PictureAsPdfRounded as PdfIcon,
  CheckCircleRounded as TickIcon,
  CancelRounded as CrossIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { usePawnMode } from "../../Context/PawnModeContext";
import {
  reportPageSx,
  reportFixedSx,
  reportResultsCardSx,
  reportTableSx,
} from "./reportLayout";
import ReportDateWindow from "./reportDateWindow";
import ReportSortControl, { DEFAULT_SORT, sortRows } from "./reportSort";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue/White Design Tokens (shared with PaymentScreen) ───────────────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const BG_ALT = "#F1F5F9";
const SUCCESS = "#059669";
const DANGER = "#DC2626";

const TODAY = () => dayjs().format("YYYY-MM-DD");
// Reports open on the running month, not on today alone: the first of the
// current month through today. A single-day-today default made every register
// look empty first thing in the morning.
const MONTH_START = () => dayjs().startOf("month").format("YYYY-MM-DD");

// Government-approved official monthly interest cap (%). Loans ARE written
// above this rate, but an OFFICIAL report must never print the true rate — it
// prints the store's master rate instead (itself clamped to the cap in Pawn
// Configuration), and the cap itself if settings could not be read. Unofficial
// reports always print the actual rate.
const OFFICIAL_RATE_CAP = 1.16;

// ── Formatting helpers ──────────────────────────────────────────────────────
const fmtINR = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};
const fmtWeight = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
};
// Metal rate per gram — usually a whole number, so trim trailing decimals.
const fmtRate = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};
// Join a set of distinct values (rates / purities) with " / ", or "—" if empty.
const joinDistinct = (list) =>
  list && list.length ? list.join(" / ") : "—";

// Run an async mapper over items with a bounded concurrency so we don't fire
// hundreds of detail requests at once when a wide date range is selected.
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = new Array(Math.min(limit, items.length))
    .fill(0)
    .map(async () => {
      while (cursor < items.length) {
        const idx = cursor++;
        results[idx] = await worker(items[idx], idx);
      }
    });
  await Promise.all(runners);
  return results;
}

// ─── Detail dialog ───────────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography sx={{ fontSize: "0.65rem", color: INK_MUTED, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.82rem", color: INK, fontWeight: 600 }}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

const PaymentDetailDialog = ({ open, onClose, row, accountsById }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  if (!row) return null;
  const p = row.payment || {};
  const li = row.loanInfo || {};
  // Prefer the authoritative loan detail; fall back to the list record.
  const pick = (key) => li[key] ?? p[key];

  // Interest amount is meaningful only when one month's interest was deducted
  // up-front — otherwise the rate alone tells the story.
  const oneMonthDeducted = Number(pick("one_month_deduct")) === 1;

  // Bank/account-transfer legs of a split payment. Leg amounts live in
  // card_amount_received_1 / _2 (legacy rows: card_amount_received).
  const bankLeg1 =
    parseFloat(
      pick("card_amount_received_1") ?? pick("card_amount_received"),
    ) || 0;
  const bankLeg2 = parseFloat(pick("card_amount_received_2")) || 0;
  const transferLegs = [];
  if (bankLeg1 > 0)
    transferLegs.push({
      amount: bankLeg1,
      accountId: pick("bank_account_id_1"),
    });
  if (bankLeg2 > 0)
    transferLegs.push({
      amount: bankLeg2,
      accountId: pick("bank_account_id_2"),
    });
  const accountName = (id) =>
    (id != null && accountsById?.get?.(String(id))?.account_name) || "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          bgcolor: BLUE,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptLongIcon />
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1 }}
            >
              Ticket {row.ticketNo}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
              {row.transactionDate
                ? dayjs(row.transactionDate).format("DD MMM YYYY")
                : ""}
              {p.payment_time ? ` · ${p.payment_time}` : ""}
            </Typography>
          </Box>
          {oneMonthDeducted && (
            <Chip
              label="1 Month Interest Deducted"
              size="small"
              sx={{
                ml: 1,
                bgcolor: "#F59E0B",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.65rem",
                letterSpacing: "0.3px",
              }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fff" }}>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: BLUE_DARK,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1,
          }}
        >
          Customer
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <DetailRow label="Name" value={row.customerName} />
          <DetailRow label="Mobile" value={row.mobile} />
          <DetailRow label="Address" value={row.address} />
        </Grid>

        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: BLUE_DARK,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1,
          }}
        >
          Loan
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <DetailRow
            label="Loan Amount"
            value={`₹ ${fmtINR(row.loanAmount)}`}
          />
          <DetailRow
            label="Interest Rate"
            value={
              row.interestRate != null && row.interestRate !== ""
                ? `${row.interestRate}%`
                : "—"
            }
          />
          {oneMonthDeducted && (
            <DetailRow
              label="Interest Amount"
              value={`₹ ${fmtINR(pick("interest_amount"))}`}
            />
          )}
          <DetailRow
            label="Gross Weight"
            value={`${fmtWeight(row.grossWeight)} g`}
          />
          <DetailRow label="Valuation" value={`₹ ${fmtINR(row.valuation)}`} />
        </Grid>

        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: BLUE_DARK,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1,
          }}
        >
          Payment Details
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <DetailRow
            label="Payment Amount"
            value={`₹ ${fmtINR(p.payment_amount)}`}
          />
          <DetailRow label="Pay Mode" value={p.pay_mode} />
          <DetailRow
            label="Cash Received"
            value={`₹ ${fmtINR(p.cash_amount_received)}`}
          />
          {transferLegs.map((leg, i) => (
            <React.Fragment key={i}>
              <DetailRow label="Pay Mode" value="Account Transfer" />
              <DetailRow
                label="Bank Account"
                value={accountName(leg.accountId)}
              />
              <DetailRow
                label="Amount Received"
                value={`₹ ${fmtINR(leg.amount)}`}
              />
            </React.Fragment>
          ))}
          <DetailRow label="Voucher Ref" value={p.voucher_ref} />
        </Grid>

        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: BLUE_DARK,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1,
          }}
        >
          Ornaments ({(row.ornaments || []).length})
        </Typography>
        {(row.ornaments || []).length === 0 ? (
          <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
            No ornament details available.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: BG_ALT }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem" }}>
                    Item
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                  >
                    Pieces
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                  >
                    Gross (g)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                  >
                    Net (g)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                  >
                    Value (₹)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.ornaments.map((o, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      {o.item_name || "—"}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: "0.78rem" }}>
                      {o.pieces ?? "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.78rem" }}>
                      {fmtWeight(o.item_gross)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.78rem" }}>
                      {fmtWeight(o.item_net)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.78rem" }}>
                      {fmtINR(o.item_value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Table columns ───────────────────────────────────────────────────────────
// The column set is built dynamically from the header toggles (Show Mobile /
// Show Rate / Show Purity), so the definitions live inside the component. Each
// column carries a `type` used both to render the body cell and to know which
// running total (if any) to print in the footer.
const numericTypes = new Set(["inr", "weight", "interest", "value"]);

// ─── Sorting ─────────────────────────────────────────────────────────────────
// What the sort button offers, and where each option reads its value from. The
// record table sorts on the payment row; the Summary layout is a per-day
// aggregate, so only the options that still mean something there are mapped
// (customer / interest have no per-day equivalent and leave it untouched).
const SORT_OPTIONS = [
  { key: "date", label: "Date" },
  { key: "amount", label: "Amount" },
  { key: "customer", label: "Customer Name" },
  { key: "voucher", label: "Pawn Ticket No" },
];
const ROW_SORT = {
  date: (r) => r.transactionDate,
  amount: (r) => r.loanAmount,
  customer: (r) => r.customerName,
  voucher: (r) => r.ticketNo,
};
const SUMMARY_SORT = {
  date: (s) => s.date,
  amount: (s) => s.amount,
  voucher: (s) => s.fromLoan,
};

// Column alignment: numbers right, the fingerprint tick/cross pair centred,
// everything else left. Shared by the header, body, footer and the PDF export
// so a column can never drift out of alignment between them.
const colAlign = (col) =>
  col.type === "fingerprint"
    ? "center"
    : numericTypes.has(col.type)
      ? "right"
      : "left";

// ─── Fingerprint cell ────────────────────────────────────────────────────────
// One mark per finger, in column order: Finger Print 1 then Finger Print 2. A
// tick means that column holds a scan, a cross means it is NULL — so both
// unscanned reads ✗ ✗, finger 1 only reads ✓ ✗, finger 2 only ✗ ✓, and both
// ✓ ✓.
const FingerprintMarks = ({ fp1, fp2 }) => (
  <Tooltip
    title={`Finger Print 1: ${fp1 ? "captured" : "not captured"} · Finger Print 2: ${
      fp2 ? "captured" : "not captured"
    }`}
  >
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
      }}
    >
      {[fp1, fp2].map((present, i) =>
        present ? (
          <TickIcon key={i} sx={{ fontSize: 17, color: SUCCESS }} />
        ) : (
          <CrossIcon key={i} sx={{ fontSize: 17, color: DANGER }} />
        ),
      )}
    </Box>
  </Tooltip>
);

// ─── Main Screen ─────────────────────────────────────────────────────────────
const PaymentReport = ({ embedded = false }) => {
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  // Pawn mode: 1 = official, 2 = unofficial. Official mode never sees
  // unofficial (status 2) records; unofficial mode shows ALL records —
  // official and unofficial alike. The filtering happens server-side via the
  // `mode` query param.
  const { mode: pawnMode } = usePawnMode() || {};

  // ── Filter state ──────────────────────────────────────────────────────────
  // A single pair of date fields serves both purposes: in "single" mode only
  // the first field is shown (one day's payments); in "range" mode both From
  // and To are shown. It opens in RANGE mode on the running month — the 1st
  // through today — because a day's worth of rows is rarely what anyone came
  // to read, and on the 1st of a month a single-day default looked empty.
  //
  // A caller (e.g. the dashboard's "Disbursed This Month" tile) may seed the
  // filters via navigation state so the report opens already scoped to some
  // other date range instead of the default month-to-date view.
  const { state: navState } = useLocation();
  const [dateMode, setDateMode] = useState(navState?.dateMode || "range");
  const [singleDate, setSingleDate] = useState(navState?.singleDate || TODAY());
  const [fromDate, setFromDate] = useState(navState?.fromDate || MONTH_START());
  const [toDate, setToDate] = useState(navState?.toDate || TODAY());
  const [customerSearch, setCustomerSearch] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");

  // ── Header view toggles (pure client-side; never trigger a re-fetch) ────────
  // Column toggles reshape the default payments table; `summary` and
  // `floorWise` swap it for an entirely different report layout. `tc` and
  // `bankPledge` are placeholders — rendered and state-tracked only, pending KT.
  const [hideTotal, setHideTotal] = useState(false); // totals row shown by default
  const [showMobile, setShowMobile] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [showPurity, setShowPurity] = useState(false);
  const [summary, setSummary] = useState(false);
  const [floorWise, setFloorWise] = useState(false);
  const [tc, setTc] = useState(false); // T&C — placeholder, no behaviour yet
  const [bankPledge, setBankPledge] = useState(false); // placeholder, no behaviour yet

  // Row order chosen from the sort button beside View. Client-side only: it
  // reorders what is already on screen and never re-fetches.
  const [sort, setSort] = useState(DEFAULT_SORT);

  // "Show All" is derived: it is ticked exactly when all three column toggles
  // are on, and clicking it flips all three together (totals stay visible).
  const showAll = showMobile && showRate && showPurity;
  const toggleShowAll = () => {
    const next = !showAll;
    setShowMobile(next);
    setShowRate(next);
    setShowPurity(next);
  };

  // ── Data state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Caches keyed by store so re-applying filters doesn't re-hit the network.
  const detailCacheRef = useRef(new Map()); // voucher_no -> { gross, valuation, ornaments, customer, loanInfo }
  const customerMapRef = useRef(new Map()); // cust_id -> customer record
  const [accountMap, setAccountMap] = useState(new Map()); // account_id -> account record
  const [floorMap, setFloorMap] = useState(new Map()); // floor_id -> floor name (for Floor-Wise Summary)

  const showSnackbar = useCallback(
    (message, severity = "info") =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  // Fetch (once per store) the customer directory so we can resolve names,
  // mobiles and addresses by cust_id without a detail call per row.
  const loadCustomerMap = useCallback(async () => {
    if (storeId == null) return;
    if (customerMapRef.current.size > 0) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}`,
        { params: { limit: 5000, sort: "ASC" } },
      );
      const list = res.data?.data || [];
      const map = new Map();
      list.forEach((c) => map.set(c.id, c));
      customerMapRef.current = map;
    } catch {
      // Non-fatal: detail calls still backfill customer info.
    }
  }, [storeId]);

  // Fetch (once per store) the master interest % from pawn settings. In
  // official mode any actual rate above the 1.16% ceiling is displayed as this
  // master rate instead; unofficial mode always shows the actual rate.
  const masterIntRef = useRef(null);
  const loadMasterInt = useCallback(async () => {
    if (storeId == null || masterIntRef.current != null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
      );
      const v = parseFloat(res.data?.data?.master_int);
      if (!isNaN(v)) masterIntRef.current = v;
    } catch {
      // Non-fatal: rates fall back to the actual stored value.
    }
  }, [storeId]);

  // Fetch (once per store) the bank-account directory so the detail dialog can
  // name the account behind each split-payment transfer leg.
  const loadAccountMap = useCallback(async () => {
    if (storeId == null) return;
    if (accountMap.size > 0) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/accounts/accounts/${storeId}/accounts-only`,
      );
      const list = res.data?.data || [];
      const map = new Map();
      // Keys normalized to strings — leg account ids may arrive as numbers.
      list.forEach((a) => map.set(String(a.account_id), a));
      setAccountMap(map);
    } catch {
      // Non-fatal: legs still render with the amount, account shows "—".
    }
  }, [storeId, accountMap]);

  // Fetch (once per store) the floor directory so the Floor-Wise Summary can
  // resolve each loan's floor_id to a readable floor name.
  const loadFloorMap = useCallback(async () => {
    if (storeId == null) return;
    if (floorMap.size > 0) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/floors/store/${storeId}`,
      );
      const list = res.data?.data || [];
      const map = new Map();
      list.forEach((f) => map.set(f.id, f.name));
      setFloorMap(map);
    } catch {
      // Non-fatal: floors fall back to a "Floor <id>" label.
    }
  }, [storeId, floorMap]);

  // Pull the per-ticket ornament + customer info (gross weight & valuation live
  // only on the loan detail). Cached by voucher number + series — the official
  // and unofficial books number independently, so the same voucher_no exists in
  // both.
  const loadTicketDetail = useCallback(
    async (voucherNo, rowStatus) => {
      const cacheKey = `${voucherNo}|${rowStatus ?? ""}`;
      if (detailCacheRef.current.has(cacheKey)) {
        return detailCacheRef.current.get(cacheKey);
      }
      const empty = {
        gross: null,
        valuation: null,
        ornaments: [],
        customer: null,
        loanInfo: null,
      };
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/loan/${encodeURIComponent(
            voucherNo,
          )}`,
          // The row's OWN series pins the lookup to the right loan.
          { params: { store_id: storeId, status: rowStatus ?? undefined } },
        );
        if (!(res.data?.success && res.data?.statusCode === 200)) {
          detailCacheRef.current.set(cacheKey, empty);
          return empty;
        }
        const ornaments = res.data.data?.ornaments_info || [];
        const gross = ornaments.reduce(
          (s, o) => s + (parseFloat(o.item_gross) || 0),
          0,
        );
        const valuation = ornaments.reduce(
          (s, o) => s + (parseFloat(o.item_value) || 0),
          0,
        );
        const detail = {
          gross,
          valuation,
          ornaments,
          customer: res.data.data?.customer_info || null,
          // Loan detail carries the split-payment legs (cash + up to two bank
          // transfers) and the one-month-deduct flag the dialog needs.
          loanInfo: res.data.data?.loan_info || null,
        };
        detailCacheRef.current.set(cacheKey, detail);
        return detail;
      } catch {
        detailCacheRef.current.set(cacheKey, empty);
        return empty;
      }
    },
    [storeId],
  );

  // ── Apply filters & build the report ──────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null) {
      showSnackbar("No store selected.", "warning");
      return;
    }

    // Validate date range.
    if (dateMode === "range" && fromDate && toDate && fromDate > toDate) {
      showSnackbar("'From' date cannot be after 'To' date.", "warning");
      return;
    }

    setLoading(true);
    try {
      await Promise.all([
        loadCustomerMap(),
        loadAccountMap(),
        loadMasterInt(),
        loadFloorMap(),
      ]);

      // Resolve the active date window from the chosen mode.
      const startDate = dateMode === "single" ? singleDate : fromDate;
      const endDate = dateMode === "single" ? singleDate : toDate;
      const wantUnofficial = Number(pawnMode) === 2;

      // The BE filters by type ("P" = payments; the receipt report will pass
      // "R"), pawn mode (official → status-1 rows only; unofficial → all rows)
      // and the date window — only the rows this screen shows come over the
      // wire.
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/payments/store/${storeId}`,
        {
          params: {
            limit: 5000,
            page: 1,
            type: "P",
            mode: wantUnofficial ? 2 : 1,
            startDate,
            endDate,
          },
        },
      );
      const all = res.data?.data || [];

      const custQuery = customerSearch.trim().toLowerCase();
      const ticketQuery = ticketSearch.trim().toLowerCase();

      const matchesCustomer = (cust) => {
        const name = (cust?.name || "").toLowerCase();
        const mobile = (cust?.mobile ?? "").toString().toLowerCase();
        return name.includes(custQuery) || mobile.includes(custQuery);
      };

      // Only the text searches remain client-side; type, mode and dates are
      // already enforced by the API.
      const filtered = all.filter((p) => {
        if (ticketQuery) {
          if (!(p.voucher_no || "").toLowerCase().includes(ticketQuery))
            return false;
        }

        // The customer directory is a once-loaded (and server-cached) snapshot
        // of active primary customers, so a newer customer — or a nominee —
        // is simply missing from it. Only a row whose customer IS in the map
        // can be rejected here; the rest are decided after enrichment, against
        // the customer the loan detail resolves.
        if (custQuery) {
          const cust = customerMapRef.current.get(p.cust_id);
          if (cust && !matchesCustomer(cust)) return false;
        }
        return true;
      });

      // Enrich each visible row with ornament totals + authoritative customer
      // info, bounded so we don't flood the backend on a wide range.
      const enrichedAll = await mapWithConcurrency(filtered, 6, async (p) => {
        const detail = await loadTicketDetail(p.voucher_no, p.status);
        const custFromMap = customerMapRef.current.get(p.cust_id);
        const cust = detail.customer || custFromMap || {};
        if (custQuery && !matchesCustomer(cust)) return null;
        // Official mode masks any actual rate above the cap behind the master
        // interest % from settings, falling back to the cap itself when
        // settings are unavailable — it must NEVER print the true above-cap
        // rate. Unofficial mode always shows the actual.
        const actualRate =
          p.loan_interest != null ? parseFloat(p.loan_interest) : "";
        const masterInt = masterIntRef.current;
        const officialRate =
          masterInt != null && masterInt <= OFFICIAL_RATE_CAP
            ? masterInt
            : OFFICIAL_RATE_CAP;
        const displayRate =
          !wantUnofficial && actualRate > OFFICIAL_RATE_CAP
            ? officialRate
            : actualRate;
        // Distinct metal rates / purities across this loan's ornaments, for the
        // optional Show Rate / Show Purity columns. Preserve first-seen order.
        const rateSet = [];
        const puritySet = [];
        (detail.ornaments || []).forEach((o) => {
          const r = fmtRate(o.metal_type_rate);
          if (r != null && !rateSet.includes(r)) rateSet.push(r);
          const pur = parseFloat(o.purity_percentage);
          if (!isNaN(pur)) {
            const label = `${pur}%`;
            if (!puritySet.includes(label)) puritySet.push(label);
          }
        });
        return {
          id: p.id,
          ticketNo: p.voucher_no,
          transactionDate: p.transaction_date,
          customerName: cust.name || "—",
          mobile: cust.mobile != null ? cust.mobile.toString() : "—",
          address: cust.pickup_address || cust.address || "—",
          loanAmount: parseFloat(p.loan_amount) || 0,
          grossWeight: detail.gross,
          valuation: detail.valuation,
          interestRate: displayRate,
          rateDisplay: joinDistinct(rateSet),
          purityDisplay: joinDistinct(puritySet),
          floorId: p.floor_id ?? null,
          tareWt: parseFloat(p.tare_wt) || 0,
          // Whether each finger was scanned when the loan was written. The
          // blobs themselves never travel — the list and the loan detail both
          // carry a has_fingerprint_* flag, and the list is the fallback for a
          // row whose detail call failed.
          fp1: !!(detail.loanInfo?.has_fingerprint_1 ?? p.has_fingerprint_1),
          fp2: !!(detail.loanInfo?.has_fingerprint_2 ?? p.has_fingerprint_2),
          ornaments: detail.ornaments,
          loanInfo: detail.loanInfo,
          payment: p,
        };
      });

      const enriched = enrichedAll.filter(Boolean);

      // Newest first.
      enriched.sort((a, b) => (b.id || 0) - (a.id || 0));
      setRows(enriched);
      if (enriched.length === 0) {
        showSnackbar("No payments found for the selected filters.", "info");
      }
    } catch (err) {
      showSnackbar(
        err?.response?.data?.statusMessage || "Failed to load payment report.",
        "error",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    storeId,
    pawnMode,
    dateMode,
    singleDate,
    fromDate,
    toDate,
    customerSearch,
    ticketSearch,
    loadCustomerMap,
    loadAccountMap,
    loadMasterInt,
    loadFloorMap,
    loadTicketDetail,
    showSnackbar,
  ]);

  // Auto-load on first mount, when the store resolves, when the pawn mode
  // flips, and — for a snappier UX — whenever a DATE is picked from the
  // calendar (single day, or From/To in range mode). The customer / voucher
  // text fields are deliberately NOT in the dependency list: those still load
  // only on the View button (or Enter) so typing doesn't fire a request per
  // keystroke.
  useEffect(() => {
    if (storeId != null) buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, pawnMode, dateMode, singleDate, fromDate, toDate]);

  const handleReset = () => {
    setDateMode("range");
    setSingleDate(TODAY());
    setFromDate(MONTH_START());
    setToDate(TODAY());
    setCustomerSearch("");
    setTicketSearch("");
    // Also clear the view toggles so the report returns to its default layout.
    setHideTotal(false);
    setShowMobile(false);
    setShowRate(false);
    setShowPurity(false);
    setSummary(false);
    setFloorWise(false);
    setTc(false);
    setBankPledge(false);
    setSort(DEFAULT_SORT);
  };

  const openDetail = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  // ── PDF export of the currently displayed payments ─────────────────────────
  const generatePDF = () => {
    if (rows.length === 0) {
      showSnackbar("Nothing to print. Load some payments first.", "warning");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    // Human-readable description of the active period.
    const periodLabel =
      dateMode === "single"
        ? dayjs(singleDate).format("DD MMM YYYY")
        : `${dayjs(fromDate).format("DD MMM YYYY")} to ${dayjs(toDate).format(
            "DD MMM YYYY",
          )}`;

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(currentStoreInfo?.name || "Payment Report", pageWidth / 2, 12, {
      align: "center",
    });

    const reportTitle =
      activeView === "summary"
        ? "Loan Payments Summary"
        : activeView === "floor"
          ? "Floorwise Summary"
          : "Payment Report";

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`${reportTitle} — ${periodLabel}`, pageWidth / 2, 18, {
      align: "center",
    });

    const filterBits = [];
    if (customerSearch.trim())
      filterBits.push(`Customer: ${customerSearch.trim()}`);
    if (ticketSearch.trim()) filterBits.push(`Ticket: ${ticketSearch.trim()}`);
    doc.setFontSize(8);
    doc.text(
      `${rows.length} record${rows.length === 1 ? "" : "s"}${
        filterBits.length ? ` | ${filterBits.join(" | ")}` : ""
      } | Generated ${dayjs().format("DD MMM YYYY HH:mm")}`,
      margin,
      24,
    );

    // A cell value for the PDF (mirrors the on-screen formatCell but uses "-").
    const pdfVal = (col, r) => {
      const v = r[col.key];
      switch (col.type) {
        // The PDF's built-in fonts have no tick/cross glyphs, so the same
        // per-finger yes/no is spelled out instead.
        case "fingerprint":
          return `${r.fp1 ? "Y" : "N"} / ${r.fp2 ? "Y" : "N"}`;
        case "date":
          return v ? dayjs(v).format("DD-MM-YYYY") : "-";
        case "inr":
          return v == null ? "-" : fmtINR(v);
        case "weight":
          return v == null ? "-" : fmtWeight(v);
        case "interest":
          return v === "" || v == null ? "-" : `${v}%`;
        default:
          return v == null || v === "—" ? "-" : String(v);
      }
    };

    // ── Build head / body / column styles for the active view ──────────────
    let head, body, colStyles;

    if (activeView === "summary") {
      head = [
        ["Sl No", "Date", "From Loan No", "To Loan No", "Loan Amount (Rs)"],
      ];
      body = displaySummaryRows.map((s, idx) => [
        idx + 1,
        s.date ? dayjs(s.date).format("DD-MM-YYYY") : "-",
        s.fromLoan,
        s.toLoan,
        fmtINR(s.amount),
      ]);
      if (!hideTotal)
        body.push([
          { content: "Total", colSpan: 4, styles: { fontStyle: "bold" } },
          {
            content: fmtINR(summaryTotal),
            styles: { fontStyle: "bold", halign: "right" },
          },
        ]);
      colStyles = {
        0: { halign: "center", cellWidth: 16 },
        4: { halign: "right" },
      };
    } else if (activeView === "floor") {
      head = [
        [
          "Floor",
          "No of Pawn Ticket",
          "Weight of Jewels (g)",
          "Tare Wt (g)",
          "Extra Tare (g)",
          "Total Wt (g)",
        ],
      ];
      body = floorRows.map((f) => [
        f.name,
        f.count,
        fmtWeight(f.gross),
        fmtWeight(f.tare),
        "-",
        fmtWeight(f.total),
      ]);
      if (!hideTotal)
        body.push([
          { content: "Total", styles: { fontStyle: "bold" } },
          {
            content: floorTotals.count,
            styles: { fontStyle: "bold", halign: "right" },
          },
          {
            content: fmtWeight(floorTotals.gross),
            styles: { fontStyle: "bold", halign: "right" },
          },
          {
            content: fmtWeight(floorTotals.tare),
            styles: { fontStyle: "bold", halign: "right" },
          },
          { content: "-", styles: { fontStyle: "bold", halign: "right" } },
          {
            content: fmtWeight(floorTotals.total),
            styles: { fontStyle: "bold", halign: "right" },
          },
        ]);
      colStyles = {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      };
    } else {
      // Default payments table — columns match the dynamic on-screen set.
      head = [["#", ...columns.map((c) => c.label.replace("₹", "Rs"))]];
      body = displayRows.map((r, idx) => [
        idx + 1,
        ...columns.map((c) => pdfVal(c, r)),
      ]);
      if (!hideTotal) {
        const totalRow = ["Totals", ...columns.map(() => "")];
        columns.forEach((c, i) => {
          if (c.total) totalRow[i + 1] = formatColTotal(c);
        });
        body.push(
          totalRow.map((content, i) => ({
            content,
            styles: {
              fontStyle: "bold",
              halign: i > 0 ? colAlign(columns[i - 1]) : "left",
            },
          })),
        );
      }
      colStyles = { 0: { halign: "center", cellWidth: 10 } };
      columns.forEach((c, i) => {
        colStyles[i + 1] = { halign: colAlign(c) };
      });
    }

    doc.autoTable({
      startY: 30,
      head,
      body,
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: 1.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        textColor: [15, 23, 42],
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.5,
      },
      columnStyles: colStyles,
      margin: { left: margin, right: margin },
    });

    const fileLabel =
      dateMode === "single"
        ? dayjs(singleDate).format("YYYY-MM-DD")
        : `${dayjs(fromDate).format("YYYY-MM-DD")}_to_${dayjs(toDate).format(
            "YYYY-MM-DD",
          )}`;
    doc.save(`Payment_Report_${fileLabel}.pdf`);
  };

  // ── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.loan += r.loanAmount || 0;
        acc.gross += r.grossWeight || 0;
        acc.valuation += r.valuation || 0;
        return acc;
      },
      { loan: 0, gross: 0, valuation: 0 },
    );
  }, [rows]);

  // ── Dynamic column set for the default payments table ───────────────────────
  // Mobile / Rate / Purity are inserted only when their toggle is on; the
  // `total` key marks which columns carry a footer sum.
  const columns = useMemo(() => {
    const c = [
      { key: "ticketNo", label: "Ticket No", type: "text", strong: true },
      { key: "customerName", label: "Customer", type: "text" },
    ];
    if (showMobile) c.push({ key: "mobile", label: "Mobile", type: "text" });
    c.push({ key: "address", label: "Address", type: "address" });
    // The payment's own date, read right after the customer block.
    c.push({ key: "transactionDate", label: "Date", type: "date" });
    c.push({
      key: "loanAmount",
      label: "Loan Amt (₹)",
      type: "inr",
      total: "loan",
      strong: true,
    });
    c.push({
      key: "grossWeight",
      label: "Gross Wt (g)",
      type: "weight",
      total: "gross",
    });
    c.push({
      key: "valuation",
      label: "Valuation (₹)",
      type: "inr",
      total: "valuation",
    });
    if (showRate) c.push({ key: "rateDisplay", label: "Rate", type: "value" });
    if (showPurity)
      c.push({ key: "purityDisplay", label: "Purity", type: "value" });
    c.push({ key: "interestRate", label: "Interest", type: "interest" });
    // Always last: a tick/cross pair for the two fingerprint columns.
    c.push({ key: "fingerprints", label: "Finger Print", type: "fingerprint" });
    return c;
  }, [showMobile, showRate, showPurity]);

  // ── Summary report: one row per day with the loan-voucher span + day total ──
  // Loans are created against a running voucher sequence, so the smallest and
  // largest voucher numbers on a given date bracket that day's pledges.
  const summaryRows = useMemo(() => {
    const num = (v) => parseInt(String(v ?? "").replace(/\D/g, ""), 10);
    const byDate = new Map();
    rows.forEach((r) => {
      const d = r.transactionDate;
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push(r);
    });
    return [...byDate.keys()]
      .sort()
      .map((d) => {
        const list = byDate.get(d);
        const sorted = [...list].sort(
          (a, b) => (num(a.ticketNo) || 0) - (num(b.ticketNo) || 0),
        );
        return {
          date: d,
          fromLoan: sorted[0]?.ticketNo ?? "—",
          toLoan: sorted[sorted.length - 1]?.ticketNo ?? "—",
          amount: list.reduce((s, r) => s + (r.loanAmount || 0), 0),
        };
      });
  }, [rows]);

  const summaryTotal = useMemo(
    () => summaryRows.reduce((s, r) => s + r.amount, 0),
    [summaryRows],
  );

  // ── The rows as ORDERED for display (and for the PDF, which prints what is
  // on screen). Sorting only reorders — the totals above are unaffected. ──
  const displayRows = useMemo(() => sortRows(rows, sort, ROW_SORT), [rows, sort]);
  const displaySummaryRows = useMemo(
    () => sortRows(summaryRows, sort, SUMMARY_SORT),
    [summaryRows, sort],
  );

  // ── Floor-Wise summary: aggregate the displayed loans by floor ──────────────
  // Weight of Jewels = Σ gross weight; Total = Gross + Tare (there is no
  // separate "Extra Tare" field, so that column stays blank).
  const floorRows = useMemo(() => {
    const NO_FLOOR = "__none__";
    const byFloor = new Map();
    // Seed every floor in the store first, so floors with no pawn payments in
    // the current result set still show up as a 0 row.
    floorMap.forEach((name, id) => {
      byFloor.set(String(id), { name, count: 0, gross: 0, tare: 0 });
    });
    rows.forEach((r) => {
      const key = r.floorId == null ? NO_FLOOR : String(r.floorId);
      if (!byFloor.has(key)) {
        byFloor.set(key, {
          name:
            r.floorId == null
              ? "— (No Floor)"
              : floorMap.get(r.floorId) || `Floor ${r.floorId}`,
          count: 0,
          gross: 0,
          tare: 0,
        });
      }
      const agg = byFloor.get(key);
      agg.count += 1;
      agg.gross += r.grossWeight || 0;
      agg.tare += r.tareWt || 0;
    });
    return [...byFloor.values()].map((f) => ({
      ...f,
      total: f.gross + f.tare,
    }));
  }, [rows, floorMap]);

  const floorTotals = useMemo(
    () =>
      floorRows.reduce(
        (acc, f) => {
          acc.count += f.count;
          acc.gross += f.gross;
          acc.tare += f.tare;
          acc.total += f.total;
          return acc;
        },
        { count: 0, gross: 0, tare: 0, total: 0 },
      ),
    [floorRows],
  );

  // Which report layout is active. Summary wins over Floor-Wise if both are on.
  const activeView = summary ? "summary" : floorWise ? "floor" : "payments";

  const cellSx = {
    fontSize: "0.8rem",
    color: INK_SOFT,
    borderBottom: `1px solid ${BORDER}`,
    py: 1,
  };
  // Shared header-cell + sticky-footer-cell styling (reused across all three
  // report layouts).
  const headSx = {
    bgcolor: "#F8FAFC",
    color: INK_MUTED,
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: "nowrap",
  };
  const footSx = {
    position: "sticky",
    bottom: 0,
    bgcolor: "#F8FAFC",
    fontWeight: 800,
    color: INK,
    fontSize: "0.82rem",
    borderTop: `2px solid ${BORDER}`,
  };
  // Table area fills whatever the filters leave over — see reportLayout.js.
  const tableContainerSx = reportTableSx;

  // Format a single body cell for the dynamic payments table by column type.
  const formatCell = (col, r) => {
    const v = r[col.key];
    switch (col.type) {
      case "fingerprint":
        return <FingerprintMarks fp1={r.fp1} fp2={r.fp2} />;
      case "date":
        return v ? dayjs(v).format("DD MMM YYYY") : "—";
      case "inr":
        return v == null ? "—" : fmtINR(v);
      case "weight":
        return v == null ? "—" : fmtWeight(v);
      case "interest":
        return v === "" || v == null ? "—" : `${v}%`;
      case "value":
        return v ?? "—";
      default:
        return v ?? "—";
    }
  };

  // The footer sum for a column (only columns tagged with `total` have one).
  const formatColTotal = (col) => {
    if (col.total === "loan" || col.total === "valuation")
      return fmtINR(totals[col.total]);
    if (col.total === "gross") return fmtWeight(totals.gross);
    return "";
  };

  return (
    <Box sx={reportPageSx(embedded)}>
      {/* ── Header (hidden when embedded in the unified Reports screen) ── */}
      {!embedded && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            ...reportFixedSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <ReportIcon />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK }}
            >
              Payment Report
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              {currentStoreInfo?.name ? `${currentStoreInfo.name}` : ""}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Filters ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          mb: 2,
          bgcolor: SURFACE,
          ...reportFixedSx,
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
            {/* Date window: "Single Day" ticked = one field; unticked slides
                a "To" field open beside it. */}
            <ReportDateWindow
              label="Period"
              dateMode={dateMode}
              setDateMode={setDateMode}
              singleDate={singleDate}
              setSingleDate={setSingleDate}
              fromDate={fromDate}
              setFromDate={setFromDate}
              toDate={toDate}
              setToDate={setToDate}
            />

            {/* Customer search */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Customer"
                placeholder="Name or mobile"
                size="small"
                fullWidth
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buildReport()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: INK_MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Ticket search */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Pawn Ticket"
                placeholder="Ticket no."
                size="small"
                fullWidth
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buildReport()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReceiptLongIcon
                        sx={{ fontSize: 18, color: INK_MUTED }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={buildReport}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{
                    bgcolor: BLUE,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                  }}
                >
                  View
                </Button>
                <ReportSortControl
                  options={SORT_OPTIONS}
                  value={sort}
                  onChange={setSort}
                />
                <Tooltip title="Reset filters">
                  <IconButton
                    onClick={handleReset}
                    sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
                  >
                    <RefreshIcon sx={{ color: INK_MUTED }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          {/* ── Report options (client-side view toggles) ── */}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px dashed ${BORDER}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 0.5,
              rowGap: 0.25,
            }}
          >
            {[
              {
                label: "Hide Total",
                checked: hideTotal,
                onChange: () => setHideTotal((v) => !v),
              },
              {
                label: "Show Mobile",
                checked: showMobile,
                onChange: () => setShowMobile((v) => !v),
              },
              {
                label: "Show Rate",
                checked: showRate,
                onChange: () => setShowRate((v) => !v),
              },
              {
                label: "Show Purity",
                checked: showPurity,
                onChange: () => setShowPurity((v) => !v),
              },
              { divider: true },
              {
                label: "Summary",
                checked: summary,
                onChange: () => setSummary((v) => !v),
              },
              {
                label: "Floor Wise Summary",
                checked: floorWise,
                onChange: () => setFloorWise((v) => !v),
              },
              { divider: true },
              {
                label: "Show All",
                checked: showAll,
                onChange: toggleShowAll,
              },
              {
                label: "T & C",
                checked: tc,
                onChange: () => setTc((v) => !v),
              },
              {
                label: "Bank Pledge",
                checked: bankPledge,
                onChange: () => setBankPledge((v) => !v),
              },
            ].map((opt, i) =>
              opt.divider ? (
                <Box
                  key={`div-${i}`}
                  sx={{
                    width: "1px",
                    alignSelf: "stretch",
                    my: 0.5,
                    mx: 0.75,
                    bgcolor: BORDER,
                  }}
                />
              ) : (
                <FormControlLabel
                  key={opt.label}
                  control={
                    <Checkbox
                      size="small"
                      checked={opt.checked}
                      onChange={opt.onChange}
                      sx={{
                        py: 0.25,
                        color: INK_MUTED,
                        "&.Mui-checked": { color: BLUE },
                      }}
                    />
                  }
                  label={opt.label}
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: INK_SOFT,
                    },
                  }}
                />
              ),
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── View section (results) ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          bgcolor: SURFACE,
          ...reportResultsCardSx,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: `1px solid ${BORDER}`,
            ...reportFixedSx,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}>
            {activeView === "summary"
              ? "Loan Payments Summary"
              : activeView === "floor"
                ? "Floorwise Summary"
                : "Payments"}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`${rows.length} ${
                rows.length === 1 ? "record" : "records"
              }`}
              size="small"
              sx={{
                bgcolor: BLUE_PALE,
                color: BLUE_DARK,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={generatePDF}
              disabled={loading || rows.length === 0}
              startIcon={<PdfIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: BLUE,
                borderColor: BLUE,
                "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
              }}
            >
              Print PDF
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: BLUE }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <ReportIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
            <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
              No payments to show. Adjust the filters and press View.
            </Typography>
          </Box>
        ) : activeView === "summary" ? (
          /* ── Summary: per-day loan-voucher span + day total ── */
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Sl No</TableCell>
                  <TableCell sx={headSx}>Date</TableCell>
                  <TableCell sx={headSx}>From Loan No</TableCell>
                  <TableCell sx={headSx}>To Loan No</TableCell>
                  <TableCell align="right" sx={headSx}>
                    Loan Amount (₹)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displaySummaryRows.map((s, idx) => (
                  <TableRow
                    key={s.date}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    <TableCell sx={{ ...cellSx, color: INK_MUTED }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {s.date ? dayjs(s.date).format("DD-MM-YYYY") : "—"}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {s.fromLoan}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {s.toLoan}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellSx, fontWeight: 600 }}
                    >
                      {fmtINR(s.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} sx={footSx}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ ...footSx, color: SUCCESS }}>
                      {fmtINR(summaryTotal)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        ) : activeView === "floor" ? (
          /* ── Floor-Wise summary: loans aggregated by floor ── */
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Floor</TableCell>
                  <TableCell align="right" sx={headSx}>
                    No of Pawn Ticket
                  </TableCell>
                  <TableCell align="right" sx={headSx}>
                    Weight of Jewels (g)
                  </TableCell>
                  <TableCell align="right" sx={headSx}>
                    Tare Wt (g)
                  </TableCell>
                  <TableCell align="right" sx={headSx}>
                    Extra Tare (g)
                  </TableCell>
                  <TableCell align="right" sx={headSx}>
                    Total Wt (g)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {floorRows.map((f, idx) => (
                  <TableRow
                    key={idx}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {f.name}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {f.count}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtWeight(f.gross)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtWeight(f.tare)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      —
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellSx, fontWeight: 600 }}
                    >
                      {fmtWeight(f.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    <TableCell sx={footSx}>Total</TableCell>
                    <TableCell align="right" sx={footSx}>
                      {floorTotals.count}
                    </TableCell>
                    <TableCell align="right" sx={footSx}>
                      {fmtWeight(floorTotals.gross)}
                    </TableCell>
                    <TableCell align="right" sx={footSx}>
                      {fmtWeight(floorTotals.tare)}
                    </TableCell>
                    <TableCell align="right" sx={footSx}>
                      —
                    </TableCell>
                    <TableCell align="right" sx={{ ...footSx, color: SUCCESS }}>
                      {fmtWeight(floorTotals.total)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        ) : (
          /* ── Default payments table with dynamic columns ── */
          <TableContainer sx={tableContainerSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>#</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={colAlign(col)} sx={headSx}>
                      {col.label}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={headSx}>
                    View
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((r, idx) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    <TableCell sx={{ ...cellSx, color: INK_MUTED }}>
                      {idx + 1}
                    </TableCell>
                    {columns.map((col) =>
                      col.type === "address" ? (
                        <TableCell
                          key={col.key}
                          sx={{
                            ...cellSx,
                            maxWidth: 200,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Tooltip title={r.address || ""}>
                            <span>{r.address}</span>
                          </Tooltip>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={col.key}
                          align={colAlign(col)}
                          sx={{
                            ...cellSx,
                            ...(col.type === "date"
                              ? { whiteSpace: "nowrap" }
                              : {}),
                            ...(col.strong
                              ? { fontWeight: 700, color: INK }
                              : {}),
                          }}
                        >
                          {formatCell(col, r)}
                        </TableCell>
                      ),
                    )}
                    <TableCell align="center" sx={cellSx}>
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => openDetail(r)}
                          sx={{ color: BLUE }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    {/* Cell under the "#" column */}
                    <TableCell sx={footSx} />
                    {columns.map((col, i) => (
                      <TableCell
                        key={col.key}
                        align={colAlign(col)}
                        sx={{
                          ...footSx,
                          ...(col.total === "loan" || col.total === "valuation"
                            ? { color: SUCCESS }
                            : {}),
                        }}
                      >
                        {col.total
                          ? formatColTotal(col)
                          : i === 0
                            ? "Totals"
                            : ""}
                      </TableCell>
                    ))}
                    {/* Cell under the "View" column */}
                    <TableCell sx={footSx} />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaymentDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        row={detailRow}
        accountsById={accountMap}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentReport;
